import cx from 'classnames'
import {
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'

import { ErrorFallback } from '../../feedback/errorFallback/ErrorFallback'
import { paperStyles } from '../../libs/paperStyles'
import { zIndexStyles } from '../../libs/zIndexStyles'

type Props = {
  /**
   * Logo on the TopBar that is shown in the left corner
   */
  logo?: ReactNode

  /**
   * Title that in the TopBar that is shown next to the logo with a divider inbetween
   */
  title?: string

  /**
   * Content that will be displayed in the left side of the TopBar.
   */
  childrenLeft?: ReactNode

  /**
   * Children that will be displayed in the right side of the TopBar.
   */
  childrenRight?: ReactNode

  /**
   * Optional className for the TopBar
   */
  className?: ComponentProps<'div'>['className']
}

/**
 * Component for displaying a top bar with a logo, title and navigation
 */
export function TopBar({
  logo,
  childrenLeft,
  childrenRight,
  title,
  className = '',
}: Props) {
  const topBarRef = useRef<HTMLDivElement>(null)
  const [topBarHeight, setTopBarHeight] = useState('0px')

  // Combination of layouteffect and ResizeObserver to efficiently measure and update the height of the TopBar without causing layout thrashing
  useLayoutEffect(() => {
    const element = topBarRef.current
    if (!element) {
      return
    }

    let rafId: number | undefined

    const updateHeight = () => {
      // Avoid scheduling a new RAF if one is already pending
      if (rafId !== undefined) {
        return
      }

      // Defer height update to avoid layout thrashing
      rafId = requestAnimationFrame(() => {
        rafId = undefined
        setTopBarHeight(`${element.clientHeight}px`)
      })
    }

    // Use ResizeObserver to avoid forced reflow on every measurement
    const resizeObserver = new ResizeObserver(() => {
      updateHeight()
    })

    resizeObserver.observe(element)

    // Initial measurement
    updateHeight()

    return () => {
      resizeObserver.disconnect()
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [])

  return (
    <div
      className={cx(
        'flex min-w-0 items-center gap-x-2 px-2 py-1',
        zIndexStyles('bars'),
        paperStyles({ borders: ['bottom'] }),
        className,
      )}
      ref={topBarRef}
      style={{ '--topBar-height': topBarHeight } as CSSProperties}
    >
      {(logo || title) && (
        <>
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            {logo}
          </ErrorBoundary>
          {title && (
            <div className="pr-2 text-xl font-light dark:text-primary-100">
              {title}
            </div>
          )}
        </>
      )}
      {childrenLeft}
      <div className="contents [&>*:first-child]:ml-auto">{childrenRight}</div>
    </div>
  )
}
